<template>
  <HirePageHeaderAlerts
    v-if="viewingAsClient || hasWorkerCapabilities"
    :hire-id="hireId"
    @switch-tab="emit('switchTab', $event)"
  />
  <PageHeader>
    <template #header-breadcrumbs>
      <HireHeaderBreadcrumbs
        class="tw-mb-4"
        :back-url="backLink.url"
        :back-label="backLink.label"
        :number="hire?.number"
      >
        <template #actions>
        <WdsStack v-if="!loading" gap-sm>
          <template v-if="hire?.activeStatus !== HireActiveStatus.Cancelled">
            <FeatureFlagged name="ai-conversation-chat">
              <AiChat
                :context-type="AiChatContext.Hire"
                :context-id="hire?.number ? Number(hire.number) : 0"
              >
                {{ $t('Assistant') }}
              </AiChat>
            </FeatureFlagged>
            <WdsMenu placement="bottom-end" manual-trigger z-index="10">
              <template #trigger="{ toggle }">
                <WdsButton data-test="menu-manage" @click="toggle">
                  <WdsStack align-center gap-custom="4px">
                    <template v-if="viewingAsSupplier || isWorker">
                      <WdsIcon name="overflow-menu-horizontal" :size="20" />
                    </template>
                    <template v-else>
                      {{ $t('Manage') }}
                      <WdsIcon name="chevron-down" :size="16" />
                    </template>
                  </WdsStack>
                </WdsButton>
              </template>

              <template v-if="viewingAsClient">
                <template v-if="contractMenuOptions.shouldShow">
                  <WdsMenuOption
                    v-if="contractMenuOptions.canEditContract"
                    @click="openContractDrawer('edit')"
                  >
                    <WdsIcon :size="16" name="edit" />
                    {{ $t('Edit contract details') }}
                  </WdsMenuOption>

                  <WdsMenuOption
                    v-if="contractMenuOptions.canChangeContractType"
                    :href="changeContractTypeUrl"
                  >
                    <WdsIcon :size="16" name="report" />
                    {{ $t('Change engagement type') }}
                  </WdsMenuOption>

                  <WdsMenuDivider />
                </template>

                <WdsMenuOption
                  :href="contractDownloadBlocked ? undefined : `/contract/${hire?.id}/pdf`"
                  :disabled="contractDownloadBlocked"
                  data-test="option-download-contract"
                >
                  <WdsIcon :size="16" name="download" />
                  <WdsStack direction-column>
                    {{ $t('Download contract') }}
                    <WdsText
                      v-if="contractDownloadBlocked"
                      xs
                      secondary
                      data-test="download-contract-blocked-reason"
                    >
                      {{
                        $t(
                          'Available once all required steps are complete, as the contract depends on them.',
                        )
                      }}
                    </WdsText>
                  </WdsStack>
                </WdsMenuOption>

                <WdsMenuOption
                  v-if="
                    hire?.activeStatus === HireActiveStatus.Terminated ||
                    hire?.activeStatus === HireActiveStatus.Ended
                  "
                  :href="
                    laravelNamedRoute(namedRoutes.frontend.user.profile.hiredirect, {
                      freelancer: hire?.worker?.id,
                    })
                  "
                  data-test="option-terminate-re-hire"
                >
                  <WdsIcon :size="16" name="restart" />
                  {{ $t('Re-hire') }}
                </WdsMenuOption>

                <WdsMenuOption
                  v-if="hire?.hasMilestones"
                  data-test="option-manage-milestones"
                  @click="openMilestonesModalHandler"
                >
                  <WdsIcon :size="16" name="list-checked" />
                  {{ $t('Manage milestones') }}
                </WdsMenuOption>

                <WdsMenuOption
                  v-if="user.hasPermission('manage-contracts') && canAttributeRecruiter"
                  @click="openAttributeRecruiterModal"
                >
                  <WdsIcon :size="16" name="group" />
                  {{
                    hire?.recruiter ? $t('Manage staffing agency') : $t('Attribute staffing agency')
                  }}
                </WdsMenuOption>

                <template v-if="user.hasPermission('manage-contracts')">
                  <WdsMenuOption
                    v-if="hire?.canTerminateContract"
                    theme="DESTRUCTIVE"
                    data-test="option-terminate-hire"
                    @click="openTerminateContractModal"
                  >
                    <WdsIcon :size="16" name="close-outline" />
                    {{ $t('Terminate hire') }}
                  </WdsMenuOption>

                  <WdsMenuOption
                    v-else-if="hire?.canCancelContract"
                    theme="DESTRUCTIVE"
                    @click="openCancelHireModal"
                  >
                    <WdsIcon :size="16" name="close-outline" />
                    {{ $t('Cancel hire') }}
                  </WdsMenuOption>
                </template>
              </template>
              <template
                v-if="(viewingAsSupplier || isWorker) && viewerConfirmsContract(hire ?? {})"
              >
                <WdsMenuOption
                  data-test="option-review-contract"
                  @click="openContractDrawer('show')"
                >
                  <WdsIcon :size="16" name="document" />
                  {{ $t('Review contract') }}
                </WdsMenuOption>
                <WdsMenuOption
                  v-if="
                    !isClientHireWithSupplier &&
                    (hire?.activeStatus === HireActiveStatus.Offered || hasPendingContractChanges)
                  "
                  theme="SUCCESS"
                  data-test="option-accept-contract"
                  :disabled="!canAcceptContract"
                  @click="openContractDrawer('accept')"
                >
                  <WdsIcon :size="16" name="checkbox-checked" />
                  {{
                    viewerSignsContract(hire ?? {})
                      ? $t('Accept and sign contract')
                      : $t('Accept contract')
                  }}
                </WdsMenuOption>
                <WdsMenuOption
                  v-if="
                    hire?.activeStatus === HireActiveStatus.Offered && !isClientHireWithSupplier
                  "
                  theme="DESTRUCTIVE"
                  data-test="option-decline-job"
                  @click="openDeclineContractModal"
                >
                  <WdsIcon :size="16" name="close-outline" />
                  <WdsStack direction-column gap-xs>
                    <span>{{ $t('Decline contract') }}</span>
                  </WdsStack>
                </WdsMenuOption>
              </template>
            </WdsMenu>
          </template>

          <WdsButton
            v-if="hire?.conversation?.viewerCanAccess"
            primary
            :href="hire?.conversation?.url"
          >
            {{ $t('Message') }}
          </WdsButton>
        </WdsStack>
        </template>
      </HireHeaderBreadcrumbs>
    </template>
    <template #header-title>
      <div v-if="!loading" class="tw-line-clamp-2">
        {{ hire?.job?.name ?? $t('Hire overview') }}
      </div>
    </template>
    <template #header-description>
      <div v-if="loading" class="header-loading-container">
        <Loading title />
        <WdsStack class="tw-w-1/2">
          <Loading />
          <Loading avatar />
        </WdsStack>
      </div>

      <HireHeaderSummary
        v-else-if="hire"
        :status-class="{ 'tw-max-w-[320px]': isOwnersExpanded }"
        :status="hire.activeStatus"
        :start="timeLine.start"
        :end="timeLine.end"
        :engagement-type-label="
          hire.engagementType && hire.engagementType !== EngagementType.Pending
            ? hire.engagementTypeLabel
            : null
        "
        :company="viewingAsSupplier || isWorker ? hire.company : null"
      >
        <template #status>
          <WdsText secondary>
            <HireOwners v-if="viewingAsClient" v-model:expanded="isOwnersExpanded" :owners="owners">
              <template #prefix>{{ $t('Owners') }}:</template>
              <template #actions>
                <WdsButtonGhost
                  v-if="!isWorker"
                  secondary
                  :href="
                    laravelNamedRoute('yjob.custom-fields', {
                      yjob: hire.job?.id,
                      // eslint-disable-next-line vue/camelcase
                      from_hire: hire.id,
                      // eslint-disable-next-line vue/camelcase
                      redirect_uri: laravelNamedRoute('hires.hire.view', {
                        hire: hire.id,
                        tab: 'overview',
                      }),
                    })
                  "
                >
                  <WdsIcon name="edit" :size="16" />
                </WdsButtonGhost>
              </template>
            </HireOwners>
          </WdsText>
        </template>
        <template #company-tags>
          <WdsTag v-if="viewingAsSupplier && sourceHireClient" sm :label="$t('MSP')" />
        </template>
        <template #company>
          <div v-if="viewingAsSupplier && sourceHireClient">
            <WdsTag
              success
              :title="$t('Client: :client', { client: sourceHireClient.name })"
              data-test="hire-client-chip"
            >
              <WdsStack gap-xs justify-start align-center>
                <WdsAvatar
                  :path="getAvatarPath(sourceHireClient.avatar, sourceHireClient.id)"
                  xs
                />
                <span class="tw-text-sm tw-font-medium">
                  {{ sourceHireClient.name }}
                </span>
              </WdsStack>
            </WdsTag>
          </div>
        </template>
        <template v-if="isCompany || isRecruiter" #worker>
          <WdsButtonGhost class="tw-p-1" @click="openWorkerDetailPanel">
            <WdsStack align-center gap-md>
              <WdsAvatarLabel>
                <template #avatar>
                  <div class="tw-relative">
                    <WdsAvatar :path="getAvatarPath(hire.worker.avatar, hire.worker.id)" md />
                  </div>
                </template>

                <WdsStack gap-xs justify-start align-center>
                  <WdsText :text="getWorkerDisplayName(hire.worker.name)" large />
                  <WdsIcon v-if="hire.recruiter" name="group" :size="14" />
                </WdsStack>
                <WdsStack direction-column gap-xs align-start>
                  <WdsText
                    v-if="hire.worker.jobTitle"
                    sm
                    secondary
                    class="tw-text-left tw-line-clamp-2"
                  >
                    {{ hire.worker.jobTitle }}
                  </WdsText>
                  <div v-if="hire.recruiter">
                    <WdsTag sm>{{ hire.recruiter.name }}</WdsTag>
                  </div>
                </WdsStack>
              </WdsAvatarLabel>
              <WdsIcon name="chevron-right" :size="16" />
            </WdsStack>
          </WdsButtonGhost>
        </template>
      </HireHeaderSummary>
    </template>
  </PageHeader>

  <WdsDrawer
    v-if="hasCompanyCapabilities || viewingAsSupplier"
    v-model:show="showWorkerDetailPanel"
    position="right"
    width="420px"
    padding="0"
  >
    <WorkerDetailPanel
      v-if="hire"
      :worker-id="hire.worker.id"
      :viewer-account-id="hire.company.id"
      @close="showWorkerDetailPanel = false"
    />
  </WdsDrawer>

  <WdsDrawer
    v-model:show="showContractDrawer"
    position="right"
    width="700px"
    closable
    dismissible
    use-underlay
    :title="$t('Contract details')"
  >
    <Transition name="fade">
      <Contract
        v-if="hire && showContractDrawer"
        v-model:loading="contractLoading"
        :hire-id="hire.id"
        :action="contractDrawerAction"
        :contract-id="contractDrawerContractId"
        :drawer="true"
        @close="closeContractDrawer"
      />
    </Transition>
    <div v-if="contractLoading" class="tw-mt-[30%] tw-flex tw-justify-center">
      <WdsSpinner :size="40" indeterminate />
    </div>
  </WdsDrawer>

  <MilestonesModal v-if="hire?.id" v-slot="{ open }" :hire-id="hire.id">
    <div :ref="() => setMilestonesModalOpen(open)" style="display: none"></div>
  </MilestonesModal>

  <template v-if="viewingAsClient">
    <AttributeRecruiterModal
      v-if="hire?.id"
      v-model:open="showAttributeRecruiterModal"
      :hire-id="hire.id"
      @close="showAttributeRecruiterModal = false"
      @save="handleAttributeRecruiterSave"
    />

    <CancelHireModal
      v-if="hire?.id"
      v-model:open="showCancelHireModal"
      :hire-id="hire.id"
      @close="closeCancelHireModal"
      @save="handleCancelHireSave"
    />

    <ModalTerminateContract
      v-if="hire?.id"
      :is-open="showTerminateContractModal"
      :hire-id="hire.id"
      :worker-name="hire.worker.name"
      :contract-end-date="contract?.endDate || ''"
      @closed="handleTerminateContractClose"
    />
  </template>

  <DeclineContractModal
    v-if="hire?.id"
    :open="showDeclineContractModal"
    :hire-id="hire.id"
    :account-id="currentAccountId"
    @close="closeDeclineContractModal"
    @hire-canceled="handleDeclineJobSave"
  />
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { useRouter } from 'vue-router'
import {
  WdsAvatar,
  WdsAvatarLabel,
  WdsButton,
  WdsButtonGhost,
  WdsDrawer,
  WdsIcon,
  WdsMenu,
  WdsMenuDivider,
  WdsMenuOption,
  WdsSpinner,
  WdsStack,
  WdsTag,
  WdsText,
} from '@worksome/wds-components'

import { graphql, useFragment } from '@/gql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import useDate from '@/composables/useDate'
import { getAvatarPath } from '@/utils/avatar'
import { getWorkerDisplayName } from '@/utils/worker'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import HireOwners from '@/components/Hire/HireOwners.vue'
import HirePageHeaderAlerts from '@/components/Hire/HirePageHeaderAlerts.vue'
import HireHeaderBreadcrumbs from '@/components/Hire/HirePageParts/HireHeaderBreadcrumbs.vue'
import HireHeaderSummary from '@/components/Hire/HirePageParts/HireHeaderSummary.vue'
import { HireActiveStatus, AiChatContext, ComplianceName, EngagementType } from '@/gql/graphql'
import WorkerDetailPanel from '@/components/WorkerDetailPanel/WorkerDetailPanel.vue'
import PageHeader from '@/components/PageHeader.vue'
import Contract from '@/components/Contract'
import MilestonesModal from '@/components/Milestones/MilestonesModal.vue'
import AttributeRecruiterModal from '@/components/Hire/AttributeRecruiterModal.vue'
import CancelHireModal from '@/components/Hire/CancelHireModal.vue'
import DeclineContractModal from '@/components/Hire/DeclineContractModal.vue'
import ModalTerminateContract from '@/components/ModalTerminateContract.vue'
import {
  useContractDrawer,
  useAcceptContractAutoOpen,
} from '@/components/Hire/composables/useContractDrawer'
import Loading from '@/components/Dashboard/Widgets/Utils/Loading.vue'
import { useCancelHireModal } from '@/components/Hire/composables/useCancelHireModal'
import { useTerminateContractModal } from '@/components/Hire/composables/useTerminateContractModal'
import AiChat from '@/components/LLM/AiChat.vue'
import FeatureFlagged from '@/components/FeatureFlagged.vue'
import { useAccountCapabilities } from '@/composables/useAccountCapabilities'
import {
  useViewerSignsContract,
  useViewerConfirmsContract,
} from '@/components/Hire/composables/useViewerSignsContract'
import { useHireRecruiter } from '@/components/Hire/composables/useHireRecruiter'
import { useComplianceGate } from '@/components/Hire/HirePageCompliance/useComplianceGates'
import { canEditHireContract } from '@/components/Hire/contractEditing'

const { isCompany } = useAccountCapabilities()

const props = defineProps<{
  hireId: string
}>()

const emit = defineEmits<{
  (e: 'switchTab', tab: string): void
  (e: 'hireEnded'): void
}>()

const router = useRouter()

const {
  showContractDrawer,
  contractDrawerAction,
  contractDrawerContractId,
  openContractDrawer,
  closeContractDrawer,
} = useContractDrawer()
const { showCancelHireModal, openCancelHireModal, closeCancelHireModal } = useCancelHireModal()
const { showTerminateContractModal, openTerminateContractModal, closeTerminateContractModal } =
  useTerminateContractModal()

const showDeclineContractModal = ref(false)
const openDeclineContractModal = () => {
  showDeclineContractModal.value = true
}
const closeDeclineContractModal = () => {
  showDeclineContractModal.value = false
}

const currentAccountId = computed(() => window.currentAccount?.globalId ?? '')

enum PanelType {
  None = 'none',
  WorkerDetail = 'workerDetail',
  AttributeRecruiter = 'attributeRecruiter',
}

const activePanel = ref<PanelType>(PanelType.None)

const { user, hasWorkerCapabilities, hasCompanyCapabilities, isWorker, isRecruiter } =
  useAccountCapabilities()

const { viewerIsSupplier } = useHireRecruiter(props.hireId)
const viewingAsSupplier = computed(() => viewerIsSupplier.value || isRecruiter.value)
const viewingAsClient = computed(() => hasCompanyCapabilities.value && !viewerIsSupplier.value)

const { completed: offerGateCompleted } = useComplianceGate(
  props.hireId,
  ComplianceName.GateHireOffer,
)

const ContractFragment = graphql(`
  fragment ContractFields on Contract {
    id
    status
    jobName
    startDate
    endDate
  }
`)

const HireFragment = graphql(`
  fragment HireHeadingFields on Hire {
    id
    number
    activeStatus
    terminationDate
    activeContract {
      ...ContractFields
    }
    latestContract {
      ...ContractFields
    }
    conversation {
      id
      url
      viewerCanAccess
    }
    company {
      staffingAgencies {
        paginatorInfo {
          total
        }
      }
    }
    owners @include(if: $isClient) {
      ...HireOwnersList
    }
    job {
      id
      name
    }
    worker {
      id
      name
      jobTitle
      avatar
    }
    company {
      id
      name
      avatar
    }
    recruiter {
      id
      name
      avatar
    }
    recruiterManagesWorkers
    viewerIsSupplier
    supplierSignatureWaived
    hasMilestones
    canTerminateContract
    canCancelContract
    hasDraftContract
    pendingContractChanges
    hireCreatedApprovalState: currentApprovalState(trigger: HIRE_CREATED) {
      id
      state
    }
    hireChangedApprovalState: currentApprovalState(trigger: HIRE_CHANGED) {
      id
      state
    }
    engagementType
    engagementTypeLabel
    viewerCanAcceptContract
    supplierHire {
      id
    }
    endClient {
      id
      name
      avatar
    }
  }
`)

const GET_HIRE = graphql(`
  query GetHireForHeader($id: ID!, $isClient: Boolean!) {
    hire(id: $id) {
      ...HireHeadingFields
    }
  }
`)

const { result, loading, refetch } = useQuery(
  GET_HIRE,
  () => ({
    id: props.hireId,
    isClient: isCompany.value && !viewerIsSupplier.value,
  }),
  {
    clientId: 'publicApi',
  },
)

const isOwnersExpanded = ref(false)
const milestonesModalOpen = ref<(() => void) | null>(null)

const showWorkerDetailPanel = computed({
  get: () => activePanel.value === PanelType.WorkerDetail,
  set: (value: boolean) => {
    activePanel.value = value ? PanelType.WorkerDetail : PanelType.None
  },
})

const showAttributeRecruiterModal = computed({
  get: () => activePanel.value === PanelType.AttributeRecruiter,
  set: (value: boolean) => {
    activePanel.value = value ? PanelType.AttributeRecruiter : PanelType.None
  },
})

const contractLoading = ref(false)

const openPanel = (panelType: PanelType) => {
  activePanel.value = panelType
}

const backLink = computed(() => {
  // These links will need to be updated when we expose the new hire page for workers and recruiters
  if (isWorker.value) {
    return {
      label: $t('Your contracts'),
      url: laravelNamedRoute(namedRoutes.frontend.jobs.index.taken),
    }
  }

  if (viewingAsSupplier.value) {
    return {
      // Needs to be changed to link to the new placements table
      label: $t('Placements'),
      url: laravelNamedRoute(namedRoutes.placements.index),
    }
  }

  return {
    label: $t('All hires'),
    url: laravelNamedRoute(namedRoutes.frontend.profiles.index.engaged),
  }
})

const hire = computed(() => useFragment(HireFragment, result.value?.hire))
const sourceHireClient = computed(() => hire.value?.endClient ?? null)
const canAcceptContract = computed(() => hire.value?.viewerCanAcceptContract ?? false)
// A draft's contract is generated from the offer requirements, so it isn't final until they're done
const contractDownloadBlocked = computed(
  () =>
    hire.value?.activeStatus === HireActiveStatus.Draft &&
    !hire.value.activeContract &&
    offerGateCompleted.value !== true,
)

// Gate the ?acceptcontract=1 auto-open: only open the drawer once the hire
// query has settled and it's actually ready to accept, so blocking alerts stay
// visible otherwise.
useAcceptContractAutoOpen(loading, canAcceptContract)

const isClientHireWithSupplier = computed(() => !!hire.value?.supplierHire)
// True when the client has edited an active hire and the supplier or worker
// hasn't accepted the change yet. Lets us show the Accept action on an
// active hire, not just on an initial offer.
const hasPendingContractChanges = computed(() => hire.value?.pendingContractChanges ?? false)

// Hide the contract-acceptance options for viewers who aren't the actual
// signer on this hire — e.g. a staffing agency that's only attributed for a
// referral fee, not managing the worker. They have nothing to sign.
const viewerSignsContract = useViewerSignsContract()
const viewerConfirmsContract = useViewerConfirmsContract()

const canAttributeRecruiter = computed(() => {
  if (!hire.value) return false

  return (
    (hire.value.company?.staffingAgencies?.paginatorInfo?.total ?? 0) > 0 &&
    !hire.value.recruiterManagesWorkers
  )
})

const contract = computed(() =>
  hire.value !== null && hire.value !== undefined
    ? useFragment(ContractFragment, hire.value.activeContract ?? hire.value.latestContract)
    : null,
)

const timeLine = computed(() => {
  if (contract.value === null) {
    return { start: '', end: '' }
  }

  const start = useDate(contract.value.startDate ?? '').date.value
  const end = contract.value.endDate ? useDate(contract.value.endDate).date.value : $t('No end date')

  return { start, end }
})

const owners = computed(() => hire.value?.owners ?? [])

const openWorkerDetailPanel = () => openPanel(PanelType.WorkerDetail)
const openAttributeRecruiterModal = () => openPanel(PanelType.AttributeRecruiter)

const openMilestonesModalHandler = () => {
  if (milestonesModalOpen.value) {
    milestonesModalOpen.value()
  }
}

const setMilestonesModalOpen = (open: () => void) => {
  milestonesModalOpen.value = open
}

const handleAttributeRecruiterSave = async () => {
  // Refetch the hire data to update the UI with the new recruiter information
  await refetch()
}

const handleCancelHireSave = async () => {
  // Refetch the hire data to update the UI after cancellation
  closeCancelHireModal()
  await refetch()
  emit('hireEnded')
}

const handleTerminateContractClose = async () => {
  closeTerminateContractModal()
  // Refetch the hire data to update the UI after termination
  await refetch()
  emit('hireEnded')
}

const handleDeclineJobSave = async () => {
  closeDeclineContractModal()
  await refetch()
}

const contractMenuOptions = computed(() => {
  if (!hire.value)
    return { canEditContract: false, canChangeContractType: false, shouldShow: false }

  const isTerminated = hire.value.terminationDate !== null

  const canEditContract = canEditHireContract(hire.value.activeStatus) && !isTerminated

  const canChangeContractType =
    !isTerminated &&
    [
      HireActiveStatus.Draft,
      HireActiveStatus.Offered,
      HireActiveStatus.Ready,
      HireActiveStatus.Active,
    ].includes(hire.value.activeStatus) &&
    !hire.value.recruiterManagesWorkers &&
    !hire.value.hasMilestones

  return {
    canEditContract,
    canChangeContractType,
    shouldShow: canEditContract || canChangeContractType,
  }
})

const changeContractTypeUrl = computed(() => {
  if (!hire.value) return ''

  const params: Record<string, string> = {
    hire: hire.value.number,
  }
  params.redirect_uri = laravelNamedRoute(namedRoutes.hires.hire.view, {
    hire: props.hireId,
    tab: 'classification',
  })

  return laravelNamedRoute(namedRoutes.hires.show['contracts-type'], params)
})

const handleQueryStringActions = () => {
  const currentRoute = router.currentRoute.value
  const { query } = currentRoute
  const paramsToRemove: string[] = []

  if (query['manage-contract'] === '1' && viewingAsClient.value) {
    openContractDrawer('edit')
    paramsToRemove.push('manage-contract')
  }

  if (query['cancel-hire'] === '1' && viewingAsClient.value) {
    openCancelHireModal()
    paramsToRemove.push('cancel-hire')
  }

  if (query['terminate-hire'] === '1' && viewingAsClient.value) {
    openTerminateContractModal()
    paramsToRemove.push('terminate-hire')
  }

  if (paramsToRemove.length > 0) {
    const newQuery = { ...query }
    paramsToRemove.forEach((param) => {
      delete newQuery[param]
    })

    router.replace({
      ...currentRoute,
      query: newQuery,
    })
  }
}

watch(
  () => router.currentRoute.value.query,
  () => {
    handleQueryStringActions()
  },
)

onMounted(() => {
  handleQueryStringActions()
})
</script>

<style lang="scss" scoped>
.contract-drawer {
  position: right;
  width: 650px;
  overflow: auto;
  padding: 0px;
  background: white;
}

.contract-content .modal-content {
  box-shadow: none !important;
  border: none !important;
}

.header-loading-container {
  .loading-container-bg {
    padding-left: 0px !important;
    padding-right: 0px !important;
  }
}
</style>
